import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createSafeIndexedDbPersister, SIMPLE_VALIDATOR} from '@github-ui/safe-indexed-db-storage'
import {sendEvent} from '@github-ui/hydro-analytics'

const ISSUE_SHOW_PATTERN = /^https?:\/\/[^/]+\/([^/]+)\/([^/]+)\/issues\/(\d+)(?:\?[^#]*)?(?:#.*)?$/
const DEFAULT_TTL = 1000 * 60 * 60 * 24 * 14 // 14 days

export interface PreheatIssuesConfig {
  /**
   * Returns whether preheating is enabled.
   */
  isEnabled: () => boolean
  /**
   * Identifies the caller so the preheat endpoint can track where
   * requests originate (e.g. `'pulls'`, `'discussions'`).
   */
  source: string
  target?: () => 'relay' | 'data-router'
}

type PreheatEventBuffer = Array<{href?: string}>

/**
 * Framework-agnostic manager that preheats issue data by listening for
 * `issue-link-to-preheat` custom events and caching the responses via
 * `requestIdleCallback`.
 *
 * Usage:
 * ```ts
 * const manager = new PreheatIssuesManager({ isEnabled: () => true })
 * manager.start()
 * // …later…
 * manager.stop()
 * ```
 */
export class PreheatIssuesManager {
  #issues: Record<string, boolean> = {}
  #pending = false
  #cancelled = false
  #storage: ReturnType<typeof createSafeIndexedDbPersister>
  readonly #isEnabled: () => boolean
  readonly #source: string
  readonly #target: () => 'relay' | 'data-router'
  #abortController = new AbortController()
  #idleCallback: number | undefined
  #running = false
  #boundHandleEvent: ((e: Event) => void) | null = null

  constructor(config: PreheatIssuesConfig) {
    this.#isEnabled = config.isEnabled
    this.#source = config.source
    this.#target = config.target ?? (() => (isFeatureEnabled('issue_viewer_paved_path') ? 'data-router' : 'relay'))
    this.#storage = createSafeIndexedDbPersister({
      validator: SIMPLE_VALIDATOR,
      sendAnalyticsEvent: (eventType, target?, payload = {}) => {
        sendEvent(eventType, {...payload, target})
      },
    })
  }

  /** Begin listening for preheat events and replay any buffered events. */
  start(): void {
    if (!this.#isEnabled()) return

    this.#cancelled = false
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController()

    // Replay any buffered events that fired before we registered.
    // __preheatEventBuffer is declared globally in issue-link.ts
    const buffer = (window as Window & {__preheatEventBuffer?: PreheatEventBuffer}).__preheatEventBuffer
    if (buffer && buffer.length > 0) {
      for (const detail of buffer) {
        if (detail.href) {
          this.#addIssue(detail.href)
        }
      }
      // Clear the buffer so events are not processed twice.
      buffer.length = 0
    }

    this.#boundHandleEvent = (e: Event) => {
      this.#addIssue((e as CustomEvent).detail.href)
    }

    window.addEventListener('issue-link-to-preheat', this.#boundHandleEvent)
    if (this.#pending) this.#scheduleNextPreheat()
  }

  /** Stop listening and cancel any in-flight idle callbacks. */
  stop(): void {
    this.#cancelled = true
    this.#abortController.abort()
    if (this.#idleCallback !== undefined) {
      window.cancelIdleCallback(this.#idleCallback)
      this.#idleCallback = undefined
    }
    if (this.#boundHandleEvent) {
      window.removeEventListener('issue-link-to-preheat', this.#boundHandleEvent)
      this.#boundHandleEvent = null
    }
  }

  // ── Internal helpers ──────────────────────────────────────────────

  #addIssue(rawHref: unknown): void {
    if (!this.#isEnabled() || typeof rawHref !== 'string') return
    let href: string
    // Resolve relative DOM links before matching, and never map another origin's links to local issue endpoints.
    try {
      const url = new URL(rawHref, window.location.href)
      if (url.origin !== window.location.origin) return
      href = url.href
    } catch {
      // Link events can contain malformed URLs, such as an invalid hostname.
      return
    }
    if (!ISSUE_SHOW_PATTERN.test(href)) return
    if (href in this.#issues) return
    this.#issues[href] = true
    this.#pending = true
    this.#scheduleNextPreheat()
  }

  #scheduleNextPreheat(): void {
    if (this.#cancelled || this.#running || this.#idleCallback !== undefined || !('requestIdleCallback' in window))
      return
    const {signal} = this.#abortController

    this.#idleCallback = requestIdleCallback(() => {
      this.#idleCallback = undefined
      if (signal.aborted || this.#cancelled || !this.#pending || !this.#isEnabled()) return
      const runPreheat = async () => {
        this.#running = true
        try {
          await this.#preheatQueries()
        } finally {
          this.#running = false
          if (this.#pending) this.#scheduleNextPreheat()
        }
      }
      void runPreheat()
    })
  }

  async #preheatQueries(): Promise<void> {
    const {signal} = this.#abortController
    const issuesToPreheat = Object.keys(this.#issues).filter(href => this.#issues[href])
    if (issuesToPreheat.length === 0) {
      this.#pending = false
      return
    }
    for (const href of issuesToPreheat) {
      if (signal.aborted || !this.#isEnabled()) return
      try {
        const urlMatch = href.match(ISSUE_SHOW_PATTERN)
        if (!urlMatch) return
        const [, owner, repo, number] = urlMatch
        if (!owner || !repo || !number) return

        if (this.#target() === 'data-router') {
          const {preheatIssueViewerPrimary} = await import('./issue-viewer-query')
          if (signal.aborted || !this.#isEnabled()) return
          await preheatIssueViewerPrimary({
            owner: decodeURIComponent(owner),
            repo: decodeURIComponent(repo),
            number: Number(number),
            source: this.#source,
            signal,
          })
          continue
        }

        const currentUserLogin = getCurrentUserLogin()
        const variables = JSON.stringify({
          count: 15,
          hideTimeline: false,
          number: Number(number),
          owner,
          repo,
        })
        const cacheKey = `relay-query-${currentUserLogin}-IssueViewerViewQuery#${variables}`

        const cachedQuery = await this.#storage.getItem(cacheKey)
        if (signal.aborted || !this.#isEnabled()) return
        if (cachedQuery) continue

        const response = await fetch(`/${owner}/${repo}/issues/preheat?number=${number}&source=${this.#source}`, {
          signal,
          headers: {
            Accept: 'application/json',
            'X-GITHUB-PREHEAT': this.#source,
          },
        })
        if (response.status === 200) {
          const payload = await response.json()
          if (signal.aborted || !this.#isEnabled()) return
          await this.#storage.setItem(cacheKey, payload.cacheValue, Date.now(), DEFAULT_TTL, {
            preheat_source: this.#source,
            update_type: 'pre_heated',
          })
        } else {
          continue
        }

        // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (error) {
        // Ignore errors and continue preheating other queries
      } finally {
        if (!signal.aborted) this.#issues[href] = false
      }
    }
    this.#pending = Object.values(this.#issues).some(Boolean)
  }
}
